/**
 * Frontend Assignment Utility Functions
 * Provides helper functions to work with both legacy and new assignment formats
 */

/**
 * Get assignment range from either legacy or new format
 * @param {Object} assignment - Assignment object
 * @returns {Object} Range object with start and end
 */
export function getAssignmentRange(assignment) {
  // New structure (v1.0.5+)
  if (assignment.range) {
    return assignment.range;
  }
  
  // Legacy structure (converted on-the-fly)
  if (assignment.surah && assignment.verses) {
    return {
      start: {
        surah: assignment.surah,
        verse: assignment.verses.from
      },
      end: {
        surah: assignment.surah,
        verse: assignment.verses.to
      }
    };
  }
  
  // Fallback for incomplete data
  return {
    start: {
      surah: { id: '?', name: 'Unknown' },
      verse: '?'
    },
    end: {
      surah: { id: '?', name: 'Unknown' },
      verse: '?'
    }
  };
}

/**
 * Generate assignment title from range
 * @param {Object} assignment - Assignment object
 * @returns {string} Formatted title
 */
export function getAssignmentTitle(assignment) {
  // Use existing title if available (new assignments have this)
  if (assignment.title) {
    return assignment.title;
  }
  
  const range = getAssignmentRange(assignment);
  
  // Single surah: "Al-Fatiha (1-7)"
  if (range.start.surah.id === range.end.surah.id) {
    return `${range.start.surah.name} (${range.start.verse}-${range.end.verse})`;
  }
  
  // Multi-surah: "Al-Fatiha (5) to Al-Baqarah (10)"
  return `${range.start.surah.name} (${range.start.verse}) to ${range.end.surah.name} (${range.end.verse})`;
}

/**
 * Get formatted verse range string for display
 * @param {Object} assignment - Assignment object
 * @returns {string} Formatted verse range like "1-7" or "5 to Al-Baqarah 10"
 */
export function getAssignmentVerseRange(assignment) {
  const range = getAssignmentRange(assignment);
  
  // Single surah: "1-7"
  if (range.start.surah.id === range.end.surah.id) {
    return `${range.start.verse}-${range.end.verse}`;
  }
  
  // Multi-surah: "5 to Al-Baqarah 10"
  return `${range.start.verse} to ${range.end.surah.name} ${range.end.verse}`;
}

/**
 * Get primary surah name for assignment
 * @param {Object} assignment - Assignment object
 * @returns {string} Primary surah name
 */
export function getAssignmentSurahName(assignment) {
  const range = getAssignmentRange(assignment);
  return range.start.surah.name;
}

/**
 * Get assignment display info (backward compatible with existing UI expectations)
 * @param {Object} assignment - Assignment object
 * @returns {Object} Display info with surah name and verse range
 */
export function getAssignmentDisplayInfo(assignment) {
  const range = getAssignmentRange(assignment);
  
  return {
    surahName: range.start.surah.name,
    versesFrom: range.start.verse,
    versesTo: range.end.verse,
    title: getAssignmentTitle(assignment),
    verseRange: getAssignmentVerseRange(assignment)
  };
}